import { useQuery } from '@tanstack/react-query';
import type { AppReleaseResponse } from '@vedaunion/shared';
import { apiFetch } from './api';

/**
 * The currently published mobile build, for the /app download page.
 *
 * Served by GET /api/app/release, which reads a manifest published beside the
 * APK on the media host (see scripts/publish-apk.sh). The download URL itself is
 * a stable filename that every release overwrites — so the link never changes
 * and always resolves to the newest build — and this query is what supplies the
 * version, size, date and checksum to go with it.
 */
export function useAppRelease() {
  return useQuery<AppReleaseResponse>({
    queryKey: ['app', 'release'],
    queryFn: ({ signal }) => apiFetch<AppReleaseResponse>('/api/app/release', { signal }),
    // The server already caches the manifest for 5 minutes; no reason for the
    // browser to ask more often than that.
    staleTime: 5 * 60_000,
  });
}

/** "71.8 MB" — decimal MB, matching what a phone's download UI will say. */
export function formatBytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** "20 August 2026" in the reader's locale. */
export function formatPublished(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}
